/**
 * DOM text measurement for the designer — isolated from wp-admin / theme CSS.
 * Prefer mounting under #veyop-template-designer-root or #designer-app when present.
 */

const WRAPPER_ID = 'veyop-text-measure-root';

/** CSS reset applied to every measure node so host page styles cannot skew metrics. */
const BASE_RESET =
  'margin:0;padding:0;border:0;box-sizing:content-box;direction:ltr;unicode-bidi:isolate;';

export function getTextMeasureHost(): HTMLElement {
  return (
    document.getElementById('veyop-template-designer-root') ||
    document.getElementById('designer-app') ||
    document.body
  );
}

function ensureMeasureWrapper(): HTMLElement {
  const host = getTextMeasureHost();
  let wrap = document.getElementById(WRAPPER_ID) as HTMLElement | null;
  if (!wrap) {
    wrap = document.createElement('div');
    wrap.id = WRAPPER_ID;
    wrap.setAttribute('aria-hidden', 'true');
    wrap.style.cssText =
      'position:absolute;left:-99999px;top:0;width:auto;height:auto;overflow:visible;visibility:hidden;pointer-events:none;z-index:-1;';
    host.appendChild(wrap);
  } else if (wrap.parentElement !== host) {
    host.appendChild(wrap);
  }
  return wrap;
}

function adoptCache(el: HTMLElement | null, wrap: HTMLElement): HTMLElement | null {
  if (!el) return null;
  if (el.parentElement !== wrap) {
    wrap.appendChild(el);
  }
  return el;
}

/** Measure text dimensions using a hidden DOM element (single-line, nowrap). */
export function measureText(
  text: string,
  fontFamily: string,
  fontSize: number,
  fontWeight = 'normal',
  fontStyle = 'normal',
): { width: number; height: number } {
  const wrap = ensureMeasureWrapper();
  let cache = adoptCache(document.getElementById('__svg-text-cache') as HTMLElement | null, wrap);
  if (!cache) {
    cache = document.createElement('div');
    cache.id = '__svg-text-cache';
    wrap.appendChild(cache);
  }
  cache.style.cssText =
    BASE_RESET +
    'position:absolute;visibility:hidden;height:auto;width:auto;white-space:nowrap;left:0;top:0;';

  cache.style.fontFamily = fontFamily;
  cache.style.fontSize = `${fontSize}px`;
  cache.style.fontWeight = fontWeight;
  cache.style.fontStyle = fontStyle;
  cache.textContent = text;

  return {
    width: cache.offsetWidth + 2,
    height: cache.offsetHeight + 2,
  };
}

/**
 * Measure the natural unwrapped text bounds. Explicit newlines are preserved,
 * and the width is the longest line so new text layers hug their content.
 */
export function measureNaturalText(
  text: string,
  fontFamily: string,
  fontSize: number,
  opts?: {
    fontWeight?: string;
    fontStyle?: string;
    lineHeight?: number;
    letterSpacing?: number;
  },
): { width: number; height: number } {
  const wrap = ensureMeasureWrapper();
  let cache = adoptCache(document.getElementById('__svg-natural-text-cache') as HTMLElement | null, wrap);
  if (!cache) {
    cache = document.createElement('div');
    cache.id = '__svg-natural-text-cache';
    wrap.appendChild(cache);
  }

  const fontWeight = opts?.fontWeight ?? 'normal';
  const fontStyle = opts?.fontStyle ?? 'normal';
  const lineHeight = opts?.lineHeight ?? 1.2;
  const letterSpacing = opts?.letterSpacing ?? 0;

  cache.style.cssText =
    BASE_RESET +
    'position:absolute;visibility:hidden;height:auto;width:auto;left:0;top:0;white-space:pre;word-break:normal;';

  cache.style.fontFamily = fontFamily;
  cache.style.fontSize = `${fontSize}px`;
  cache.style.fontWeight = fontWeight;
  cache.style.fontStyle = fontStyle;
  cache.style.lineHeight = String(lineHeight);
  cache.style.letterSpacing = `${letterSpacing}px`;
  cache.style.width = 'auto';
  cache.style.maxWidth = 'none';
  cache.textContent = text || ' ';

  return {
    width: cache.scrollWidth,
    height: cache.scrollHeight,
  };
}

export type TextLayerMeasureOpts = {
  fontWeight?: string;
  fontStyle?: string;
  lineHeight?: number;
  letterSpacing?: number;
  textDecoration?: string;
  /** Matches DesignView flex `justifyContent` from `item.textAlign`. */
  textAlign?: string;
};

/** Same mapping as DesignView.vue `textAlignToJustify`. */
function textAlignToFlexJustify(align: unknown): 'flex-start' | 'center' | 'flex-end' {
  const a = String(align ?? 'center').toLowerCase();
  if (a === 'left') return 'flex-start';
  if (a === 'right') return 'flex-end';
  return 'center';
}

/**
 * Match DesignView.vue text chrome: flex + py-1 px-2 + inner shrink-wrapped nowrap line.
 * Returns outer size (drag-item width/height) without adding padding again.
 */
export function measureTextLayerBounds(
  text: string,
  fontFamily: string,
  fontSize: number,
  opts?: TextLayerMeasureOpts,
): { width: number; height: number } {
  const wrap = ensureMeasureWrapper();
  let outer = adoptCache(document.getElementById('__text-layer-bounds-cache') as HTMLElement | null, wrap);
  if (!outer) {
    outer = document.createElement('div');
    outer.id = '__text-layer-bounds-cache';
    const inner = document.createElement('span');
    outer.appendChild(inner);
    wrap.appendChild(outer);
  }

  let inner = outer.firstElementChild as HTMLElement | null;
  if (!inner || inner.tagName !== 'SPAN') {
    if (inner) outer.removeChild(inner);
    inner = document.createElement('span');
    outer.appendChild(inner);
  }

  const fontWeight = opts?.fontWeight ?? 'normal';
  const fontStyle = opts?.fontStyle ?? 'normal';
  const lineHeight = opts?.lineHeight ?? 1.2;
  const letterSpacing = opts?.letterSpacing ?? 0;
  const textDecoration = opts?.textDecoration ?? 'none';
  const justifyContent = textAlignToFlexJustify(opts?.textAlign);

  outer.style.cssText =
    'margin:0;border:0;direction:ltr;unicode-bidi:isolate;' +
    'position:absolute;visibility:hidden;left:0;top:0;' +
    'display:flex;align-items:center;' +
    'box-sizing:border-box;padding:4px 8px;width:max-content;height:auto;';

  outer.style.justifyContent = justifyContent;
  outer.style.fontFamily = fontFamily;
  outer.style.fontSize = `${fontSize}px`;
  outer.style.fontWeight = fontWeight;
  outer.style.fontStyle = fontStyle;
  outer.style.lineHeight = String(lineHeight);
  outer.style.letterSpacing = `${letterSpacing}px`;
  outer.style.textDecoration = textDecoration;

  inner.style.cssText =
    'display:inline-block;max-width:none;overflow:visible;margin:0;padding:0;white-space:nowrap;word-break:normal;';
  inner.textContent = text || ' ';

  /** Use scroll dims so intrinsic text width is never underestimated vs offset-only rounding. */
  const rawW = Math.max(outer.offsetWidth, outer.scrollWidth);
  const rawH = Math.max(outer.offsetHeight, outer.scrollHeight);
  const epsilon = 1;

  return {
    width: Math.ceil(rawW + epsilon),
    height: Math.ceil(rawH + epsilon),
  };
}

/**
 * Measure wrapped text inside a constrained box width (for text-box items).
 */
export function measureTextBox(
  text: string,
  fontFamily: string,
  fontSize: number,
  boxWidthPx: number,
  opts?: {
    fontWeight?: string;
    fontStyle?: string;
    lineHeight?: number;
    letterSpacing?: number;
  },
): { width: number; height: number } {
  const wrap = ensureMeasureWrapper();
  let cache = adoptCache(document.getElementById('__svg-text-box-cache') as HTMLElement | null, wrap);
  if (!cache) {
    cache = document.createElement('div');
    cache.id = '__svg-text-box-cache';
    wrap.appendChild(cache);
  }

  const fontWeight = opts?.fontWeight ?? 'normal';
  const fontStyle = opts?.fontStyle ?? 'normal';
  const lineHeight = opts?.lineHeight ?? 1.2;
  const letterSpacing = opts?.letterSpacing ?? 0;
  const w = Math.max(1, Math.floor(boxWidthPx));

  cache.style.cssText =
    BASE_RESET +
    'position:absolute;visibility:hidden;height:auto;left:0;top:0;white-space:pre-wrap;word-break:break-word;';

  cache.style.fontFamily = fontFamily;
  cache.style.fontSize = `${fontSize}px`;
  cache.style.fontWeight = fontWeight;
  cache.style.fontStyle = fontStyle;
  cache.style.lineHeight = String(lineHeight);
  cache.style.letterSpacing = `${letterSpacing}px`;
  cache.style.width = `${w}px`;
  cache.style.maxWidth = `${w}px`;
  cache.textContent = text;

  return {
    width: cache.scrollWidth,
    height: cache.scrollHeight,
  };
}
